import React, { useState, useEffect } from 'react';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { mathFormat } from '../../utils/common';
import DefinitionBubble from '@sg/components/DefinitionBubble';
import NumArrow from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/NumArrow';
import { indicatorValRankingFormat } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overview/PerformanceSummary/cardUtils';
import { Input } from 'tea-component';

interface MetricsBoardProps {
  title: string;
  num: number;
  textList: { value: string; text: string }[];
  dotColor?: string;  // For the colored dot
  showMoM?: boolean; // Control MoM visibility
  definition?: string;
  updateTime?: string;
  source?: string;
  precision?: number;
  unit?: string;
  isEditing?: boolean;
  onChange?: (value: any) => void;
  onBlur?: (value: any) => void;
  valueFormatter?: (value: any) => string;
  period?: string; // Period string to display below title (e.g., "2025-01~2025-10")
}

const MetricsBoard = ({
  title,
  num,
  unit,
  isEditing = false,
  textList,
  dotColor = '#4DC3FF',
  showMoM = true,
  definition,
  updateTime,
  source,
  precision,
  onChange,
  onBlur,
  valueFormatter,
  period,
}: MetricsBoardProps) => {
  const [inputValue, setInputValue] = useState<string>(String(num || ''));

  // 当num或isEditing变化时，更新inputValue
  useEffect(() => {
    setInputValue(String(num || ''));
  }, [num, isEditing]);

  const getNumConfig = (text: string) => {
    if (!text) return null;
    // if (text === null || text === undefined || text === '') {
    //   return {
    //     numContent: '',
    //     numColor: 'grayColor',
    //     numSize: 'zero',
    //     arrow: false,
    //     arrowColor: '',
    //     isShowZero: false, // This will make NumArrow show "-" instead
    //   };
    // }
    // Use indicatorValRankingFormat to get the proper formatting
    const {
      _indicatorContent,
      _indicatorSize,
      _indicatorColor,
      _isIndicatorArrow,
      _indicatprsArrowColor,
    } = indicatorValRankingFormat(text);

    // Map the indicatorValRankingFormat output to NumArrow expected format
    return {
      numContent: _indicatorContent,
      numColor: _indicatorColor,
      numSize: _indicatorSize,
      arrow: _isIndicatorArrow,
      arrowColor: _indicatprsArrowColor,
      // isShowZero: true, // Show "0" instead of "-" when value is 0
    };
  };

  return (
    <DefinitionBubble
      definition={definition}
      updateTime={updateTime}
      source={source}
      dark
      placement="top"
      arrowPointAtCenter
    >
      <div className="dbs-metrics-board">
        <div className="dbs-metrics-board-header-wrapper">
          <span
            className="dbs-metrics-board-dot"
            style={{ '--dot-color': dotColor } as React.CSSProperties}
          />
          <span>{title}</span>
        </div>
        {/* {period && ( */}
          <span className="dbs-metrics-board-period">{period}</span>
        {/* )} */}
        <div className="dbs-metrics-board-main-row">
          {isEditing ? (
            <Input
              type="text"
              value={inputValue || ''}
              style={{height: '36px', width: '180px', marginTop: '10px'}}
              placeholder="Data"
              onChange={(value) => {
                // 只允许数字、小数点和负号
                let sanitizedValue = value.replace(/[^0-9.-]/g, '');

                // 确保负号只在开头
                const hasNegativeAtStart = sanitizedValue.startsWith('-');
                sanitizedValue = sanitizedValue.replace(/-/g, '');
                if (hasNegativeAtStart) {
                  sanitizedValue = '-' + sanitizedValue;
                }

                // 确保只有一个小数点
                const dotIndex = sanitizedValue.indexOf('.');
                if (dotIndex !== -1) {
                  sanitizedValue = sanitizedValue.substring(0, dotIndex + 1) +
                    sanitizedValue.substring(dotIndex + 1).replace(/\./g, '');
                }

                setInputValue(sanitizedValue);
                onChange && onChange(sanitizedValue);
              }}
              onBlur={() => {
                // Trigger onBlur callback with current input value
                onBlur && onBlur(inputValue);
              }}
            />
          ) : (
            <span className="dbs-metrics-board-num">{`${unit === undefined ? '$' : unit }${num === null || num === undefined ? 'NA' : (valueFormatter ? valueFormatter(num) : mathFormat(num, precision))}`}</span>
          )}
          <div className="dbs-metrics-board-metrics-label">
            {textList.map((item: any, i: any) => {
              if (!showMoM) return null;

              const numArrowData = getNumConfig(item.text);

              // Don't render if the value is 0 or empty
              if (!numArrowData || numArrowData.numSize === 'zero' || !item.text || item.text === '0' || item.text === '0%') {
                return null;
              }

              return (
                <div className="dbs-metrics-board-text-flex" key={i}>
                  <div className="num-gray">{item.value}</div>
                  <NumArrow dataContent={numArrowData} type={'num'} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DefinitionBubble>
  );
};

export default MetricsBoard;
